// Contract between the signaling server and the browser client.
// Both sides import this file, so any change here is checked by the compiler on both ends.

import type { RedisAction, RedistributionState } from './redistribution.js';

export type Role = 'host' | 'participant';
export type SharePolicy = 'host' | 'all';

export interface RoomSettings {
  waitingRoom: boolean;
  muteOnEntry: boolean;
  allowUnmute: boolean;
  allowShare: SharePolicy;
  allowChat: boolean;
  allowRename: boolean;
  /** Participants may record the meeting on their device (the host always can). */
  allowRecording: boolean;
}

export const DEFAULT_ROOM_SETTINGS: RoomSettings = {
  waitingRoom: true,
  muteOnEntry: false,
  allowUnmute: true,
  allowShare: 'all',
  allowChat: true,
  allowRename: true,
  allowRecording: false,
};

export interface MeetingPublicInfo {
  id: string;
  title: string;
  scheduled: boolean;
  startTime: string | null;
  durationMinutes: number;
  hasPasscode: boolean;
  createdAt: string;
  live: boolean;
  participantCount: number;
}

export interface CreateMeetingRequest {
  title?: string;
  startTime?: string | null;
  durationMinutes?: number;
  passcode?: string;
  settings?: Partial<RoomSettings>;
}

export interface CreateMeetingResponse {
  meeting: MeetingPublicInfo;
  hostKey: string;
  passcode: string | null;
}

export interface ClientConfig {
  iceServers: RTCIceServerLike[];
  /** null = no limit (the default). */
  maxParticipants: number | null;
  /** Base URL others use to reach the server; invitation links are built from it. */
  publicUrl: string | null;
  turnConfigured: boolean;
  /**
   * How audio and video travel. "p2p": a direct connection to each participant (mesh).
   * "livekit": everyone sends once to a LiveKit media server (SFU), which forwards to the rest.
   */
  media: MediaTransportKind;
}

export type MediaTransportKind = 'p2p' | 'livekit';

export type RecordingStatus = 'on' | 'paused' | null;

export interface RTCIceServerLike {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface Participant {
  id: string;
  name: string;
  role: Role;
  audioMuted: boolean;
  videoOn: boolean;
  sharing: boolean;
  /** The sharer froze their shared content (Zoom's "Pause share"). */
  sharePaused: boolean;
  handRaised: boolean;
  cameraStreamId: string | null;
  screenStreamId: string | null;
  connected: boolean;
  joinedAt: number;
  /** Breakout room this person is in right now; null = the main session. */
  breakoutRoomId: string | null;
  /** This person is recording the meeting on their device (everyone is told). */
  recording: RecordingStatus;
}

export interface WaitingEntry {
  id: string;
  name: string;
  since: number;
}

export interface ChatMessage {
  id: string;
  from: string;
  fromName: string;
  to: 'everyone' | string;
  toName: string | null;
  text: string;
  at: number;
}

// ── Breakout rooms (Zoom's "Breakout Rooms") ──

export interface BreakoutRoom {
  id: string;
  name: string;
}

export interface BreakoutState {
  /** idle: rooms prepared or none; open: people are in rooms; closing: countdown before everyone returns. */
  status: 'idle' | 'open' | 'closing';
  rooms: BreakoutRoom[];
  /** The host's plan: participant id → room id. People can still be elsewhere (moved, chose, returned). */
  assignments: Record<string, string>;
  /** Participants may go back to the main session on their own. */
  allowReturn: boolean;
  /** Participants may pick a room themselves. */
  allowChoose: boolean;
  /** Seconds of warning before the rooms close (0 = at once). */
  countdownSeconds: number;
  /** When the rooms close (epoch ms) while status is "closing". */
  closesAt: number | null;
}

export const DEFAULT_BREAKOUT: BreakoutState = {
  status: 'idle',
  rooms: [],
  assignments: {},
  allowReturn: true,
  allowChoose: false,
  countdownSeconds: 60,
  closesAt: null,
};

export const BREAKOUT_LIMITS = { rooms: 50, roomName: 40, countdownMax: 300 } as const;

export interface BreakoutSetup {
  rooms: BreakoutRoom[];
  assignments: Record<string, string>;
  allowReturn: boolean;
  allowChoose: boolean;
  countdownSeconds: number;
}

export interface MediaStatePatch {
  audioMuted?: boolean;
  videoOn?: boolean;
  cameraStreamId?: string | null;
  screenStreamId?: string | null;
  sharePaused?: boolean;
}

export type SignalData =
  | { description: { type: 'offer' | 'answer' | 'rollback' | 'pranswer'; sdp?: string } }
  | { candidate: { candidate: string; sdpMid?: string | null; sdpMLineIndex?: number | null; usernameFragment?: string | null } | null };

export type HostAction =
  | { action: 'mute'; target: string }
  | { action: 'mute-all' }
  | { action: 'ask-unmute'; target: string }
  | { action: 'remove'; target: string }
  | { action: 'to-waiting'; target: string }
  | { action: 'admit'; target: string }
  | { action: 'admit-all' }
  | { action: 'deny'; target: string }
  | { action: 'make-host'; target: string }
  | { action: 'stop-share'; target: string }
  | { action: 'lower-hand'; target: string }
  | { action: 'lock'; locked: boolean }
  | { action: 'settings'; settings: Partial<RoomSettings> }
  | { action: 'end' }
  /** Prepares or edits the rooms; while open, changed assignments move people at once. */
  | { action: 'breakout-setup'; setup: BreakoutSetup }
  /** Stops someone's recording (their file is still saved on their device). */
  | { action: 'stop-recording'; target: string }
  | { action: 'breakout-open' }
  | { action: 'breakout-close'; now?: boolean }
  /** Moves someone to a room (null = main session) while the rooms are open. */
  | { action: 'breakout-move'; target: string; roomId: string | null }
  /** A message shown to everyone, in every room. */
  | { action: 'breakout-broadcast'; text: string };

export type ClientMessage =
  | {
      type: 'join';
      meetingId: string;
      name: string;
      clientId: string;
      passcode?: string;
      hostKey?: string;
      resumeToken?: string;
      media: { audioMuted: boolean; videoOn: boolean };
    }
  | { type: 'signal'; to: string; data: SignalData }
  | { type: 'media-state'; state: MediaStatePatch }
  | { type: 'share-start' }
  | { type: 'share-stop' }
  | { type: 'chat'; to: 'everyone' | string; text: string }
  | { type: 'raise-hand'; raised: boolean }
  | { type: 'reaction'; emoji: string }
  | { type: 'rename'; name: string }
  | { type: 'host'; command: HostAction }
  | { type: 'leave'; newHostId?: string }
  /** Go to a breakout room (null = back to the main session), when the host allows it. */
  | { type: 'breakout-go'; roomId: string | null }
  /** Ask the host to come to my breakout room. */
  | { type: 'breakout-help' }
  /** I started, paused or stopped recording on my device. */
  | { type: 'recording'; status: RecordingStatus }
  /** A step of the redistribution (move a coin, pass the turn…); the host's steps too. */
  | { type: 'redistribution'; action: RedisAction }
  /** LiveKit mode: the media connection dropped or the credential expired; send a fresh one. */
  | { type: 'media-token' }
  | { type: 'ping' };

export type EndReason = 'host-ended' | 'removed' | 'denied' | 'expired';

export type ServerMessage =
  | {
      type: 'joined';
      self: Participant;
      resumeToken: string;
      meeting: MeetingPublicInfo;
      participants: Participant[];
      waiting: WaitingEntry[];
      settings: RoomSettings;
      locked: boolean;
      resumed: boolean;
      breakout: BreakoutState;
      redistribution: RedistributionState;
    }
  | { type: 'waiting'; meeting: MeetingPublicInfo; resumeToken: string; selfId: string }
  | { type: 'participant-joined'; participant: Participant }
  | { type: 'participant-updated'; participant: Participant }
  | { type: 'participant-left'; id: string; name: string; reason: 'left' | 'removed' | 'moved-to-waiting' | 'timeout' }
  | { type: 'waiting-updated'; waiting: WaitingEntry[] }
  | { type: 'room-updated'; settings: RoomSettings; locked: boolean }
  | { type: 'breakout-updated'; breakout: BreakoutState }
  | { type: 'redistribution-updated'; redistribution: RedistributionState }
  | { type: 'breakout-help'; from: string; name: string; roomId: string }
  /** Your recording must stop (host's decision or recording no longer allowed). */
  | { type: 'recording-stopped'; byName: string }
  | { type: 'breakout-broadcast'; byName: string; text: string }
  | { type: 'host-changed'; hostId: string; previousHostId: string | null }
  | { type: 'signal'; from: string; data: SignalData }
  /**
   * LiveKit mode: credentials for the media room this person may be in right now (the main
   * session or their breakout room). A new one arrives on every room change.
   * url null = connect through this server's own /livekit path (same origin as the page).
   */
  | { type: 'media-room'; url: string | null; room: string; token: string }
  | { type: 'chat'; message: ChatMessage }
  | { type: 'force-mute'; byName: string }
  | { type: 'ask-unmute'; byName: string }
  | { type: 'moved-to-waiting'; byName: string }
  | { type: 'reaction'; from: string; emoji: string }
  | { type: 'notice'; level: 'info' | 'warning'; text: string }
  | { type: 'meeting-ended'; reason: EndReason; byName?: string }
  | { type: 'error'; code: ErrorCode; message: string }
  | { type: 'pong' };

export type ErrorCode =
  | 'not-found'
  | 'bad-passcode'
  | 'locked'
  | 'full'
  | 'blocked'
  | 'forbidden'
  | 'invalid'
  | 'rate-limited'
  | 'share-busy'
  | 'not-joined';

export const REACTIONS = ['👍', '👏', '❤️', '😂', '😮', '🎉'] as const;

export const LIMITS = {
  nameLength: 64,
  titleLength: 120,
  chatLength: 10000,
  passcodeLength: 32,
} as const;

/** Meeting IDs are 10 digits, shown as 3-3-4 like most conferencing tools. */
export function formatMeetingId(id: string): string {
  const d = id.replace(/\D/g, '');
  if (d.length !== 10) return d;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

/**
 * Accepts "123 456 7890", "1234567890" or any URL containing /j/<id>[?pwd=...].
 * Returns null when no meeting ID can be found.
 */
export function parseMeetingInput(input: string): { id: string; passcode: string | null } | null {
  const raw = input.trim();
  if (!raw) return null;
  const urlMatch = raw.match(/\/j\/(\d[\d\s-]{8,14}\d)/);
  if (urlMatch) {
    const id = urlMatch[1].replace(/\D/g, '');
    let passcode: string | null = null;
    const q = raw.indexOf('?');
    if (q >= 0) passcode = new URLSearchParams(raw.slice(q + 1).split('#')[0]).get('pwd');
    return id.length === 10 ? { id, passcode } : null;
  }
  const digits = raw.replace(/[\s-]/g, '');
  return /^\d{10}$/.test(digits) ? { id: digits, passcode: null } : null;
}
